import React, { useState, useMemo, useRef } from 'react';
import { groupVariablesByCategory } from '../data/sampleVariables';
import { Search, GripVertical, ChevronDown, ChevronRight, Upload } from './icons';
import * as XLSX from 'xlsx';
import { extractTemplateFromHtml } from '../utils/docxIngest';
import { convertDocxToHtml } from '../utils/docxToHtml';
import { setDraggedVariable } from './dragState';
export function VariableSidebar({ variables, setVariables, onDocxUpload, onInsertVariable }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [collapsedCategories, setCollapsedCategories] = useState(new Set());
    const [isParsingDocx, setIsParsingDocx] = useState(false);
    const excelInputRef = useRef(null);
    const docxInputRef = useRef(null);
    const filteredVariables = useMemo(() => {
        const seen = new Set();
        const uniqueList = [];
        (variables || []).forEach((v) => {
            let cat = v.category ?? 'Document Variables';
            if (typeof cat === 'string' && cat.toLowerCase() === 'other') {
                cat = 'Document Variables';
            }
            const norm = (v.label || v.key || '').trim().toLowerCase();
            const id = `${cat}:${norm}`;
            if (norm && !seen.has(id)) {
                seen.add(id);
                uniqueList.push({ ...v, category: cat });
            }
        });
        const list = [...uniqueList].sort((a, b) => (a.label || a.key || '').localeCompare(b.label || b.key || '', undefined, { numeric: true, sensitivity: 'base' }));
        if (!searchQuery.trim())
            return list;
        const q = searchQuery.toLowerCase();
        return list.filter((v) => v.label.toLowerCase().includes(q) ||
            v.key.toLowerCase().includes(q) ||
            (v.description?.toLowerCase().includes(q) ?? false));
    }, [searchQuery, variables]);
    const grouped = useMemo(() => groupVariablesByCategory(filteredVariables), [filteredVariables]);
    const toggleCategory = (category) => {
        setCollapsedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(category)) {
                next.delete(category);
            }
            else {
                next.add(category);
            }
            return next;
        });
    };
    const handleDragStart = (e, variable) => {
        const dragData = {
            key: variable.key,
            label: variable.label,
            isInternal: false,
        };
        setDraggedVariable(dragData);
        document.body.classList.add('is-dragging-variable');
        try {
            e.dataTransfer.setData('application/notice-variable', JSON.stringify(dragData));
            e.dataTransfer.setData('text/plain', `<<${variable.label}>>`);
            e.dataTransfer.effectAllowed = 'copy';
        }
        catch {
            // ignore
        }
        try {
            const ghost = document.createElement('div');
            ghost.className = 'drag-ghost';
            ghost.textContent = `<<${variable.label}>>`;
            document.body.appendChild(ghost);
            e.dataTransfer.setDragImage(ghost, 0, 0);
            requestAnimationFrame(() => {
                ghost.remove();
            });
        }
        catch {
            // ignore
        }
    };
    const handleDragEnd = () => {
        document.body.classList.remove('is-dragging-variable');
        setTimeout(() => {
            setDraggedVariable(null);
        }, 150);
    };
        const processDocxHtml = (html) => {
        try {
            // Parsing and <<variable>> extraction now live in utils/docxIngest.js, so the
            // Notice Generation flow - which opens an already-uploaded template
            // programmatically - ingests documents exactly as this file picker does.
            const { html: parsedHtml, variableNames } = extractTemplateFromHtml(html);
            if (onDocxUpload) onDocxUpload(parsedHtml, variableNames);
        }
        catch (err) {
            console.error('Error inserting DOCX HTML:', err);
            alert('Failed to process DOCX file into the editor: ' + (err.message || String(err)));
        }
        finally {
            setIsParsingDocx(false);
            if (docxInputRef.current) {
                docxInputRef.current.value = '';
            }
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        if (file.name.toLowerCase().endsWith('.docx')) {
            const reader = new FileReader();
            reader.onload = async (evt) => {
                const arrayBuffer = evt.target?.result;
                if (!arrayBuffer)
                    return;
                setIsParsingDocx(true);

                // The source raced a Vite-created module Worker against a 2s in-thread
                // fallback. Webpack 4 (react-scripts 3.4.1) cannot build
                // `new Worker(new URL(..., import.meta.url))`, so the same conversion -
                // including the Word paragraph-style extraction the fallback skipped -
                // now runs inline. See utils/docxToHtml.js.
                try {
                    processDocxHtml(await convertDocxToHtml(arrayBuffer));
                } catch (parseErr) {
                    console.error('DOCX parse error:', parseErr);
                    alert('Failed to parse DOCX file: ' + (parseErr.message || String(parseErr)));
                    setIsParsingDocx(false);
                    if (docxInputRef.current) {
                        docxInputRef.current.value = '';
                    }
                }
            };
            reader.readAsArrayBuffer(file);
            return;
        }
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const arrayBuffer = evt.target?.result;
                const wb = XLSX.read(arrayBuffer, { type: 'array', sheetRows: 10 });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                // Extract headers (first row)
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
                let headers = [];
                for (const row of data) {
                    if (Array.isArray(row) && row.length > 0) {
                        const possibleHeaders = row.filter(h => typeof h === 'string' && h.trim() !== '');
                        if (possibleHeaders.length > 0) {
                            headers = possibleHeaders;
                            break;
                        }
                    }
                }
                if (headers.length > 0) {
                    const seenHeaders = new Set();
                    const newVariables = [];
                    headers.forEach(header => {
                        const cleanHeader = String(header || '').trim();
                        const norm = cleanHeader.toLowerCase();
                        if (cleanHeader && !seenHeaders.has(norm)) {
                            seenHeaders.add(norm);
                            newVariables.push({
                                key: cleanHeader.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase(),
                                label: cleanHeader,
                                category: 'Excel Data',
                                type: 'string',
                                description: `Imported from ${file.name}`
                            });
                        }
                    });
                    newVariables.sort((a, b) => (a.label || a.key || '').localeCompare(b.label || b.key || '', undefined, { numeric: true, sensitivity: 'base' }));
                    setVariables(prevVars => {
                        const otherVars = (prevVars || []).filter(v => v.category !== 'Excel Data');
                        return [...otherVars, ...newVariables];
                    });
                }
                else {
                    alert('Could not find any valid headers in the Excel file.');
                }
            }
            catch (err) {
                console.error('Error parsing Excel file:', err);
                alert('Failed to parse Excel file. Please ensure it is a valid .xlsx file.');
            }
            // Reset input so the same file can be uploaded again if needed
            if (excelInputRef.current) {
                excelInputRef.current.value = '';
            }
        };
        reader.readAsArrayBuffer(file);
    };
    const getVariableIcon = (variable) => {
        const trimmed = (variable.label || variable.key || '').trim();
        if (!trimmed)
            return 'Aa';
        const firstChar = trimmed[0];
        if (/[a-zA-Z]/.test(firstChar)) {
            return `${firstChar.toUpperCase()}${firstChar.toLowerCase()}`;
        }
        return firstChar.toUpperCase();
    };
    return (<aside className="variable-sidebar" id="variable-sidebar">
      <div className="sidebar-header">
        <h2 className="sidebar-title">Data Fields</h2>
        <p className="sidebar-subtitle">Drag into the editor</p>
      </div>

      <div className="sidebar-search">
        <Search size={16} className="search-icon"/>
        <input id="variable-search" type="text" placeholder="Search variables..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-input"/>
      </div>

      <div className="sidebar-add-custom" style={{ padding: '0 16px 12px', display: 'flex', gap: '8px' }}>
        <input type="file" accept=".xlsx, .xls" style={{ display: 'none' }} ref={excelInputRef} onChange={handleFileUpload}/>
        <input type="file" accept=".docx" style={{ display: 'none' }} ref={docxInputRef} onChange={handleFileUpload}/>
        <button type="button" onClick={() => excelInputRef.current?.click()} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px', background: '#eef2ff', color: '#4f46e5', border: '1px dashed #a5b4fc', borderRadius: '6px', cursor: 'pointer', fontWeight: 500, fontSize: '13px' }} title="Upload Excel sheet for variables">
          <Upload size={14}/> XLSX
        </button>
        <button type="button" onClick={() => docxInputRef.current?.click()} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px', background: '#fdf4ff', color: '#c026d3', border: '1px dashed #f5d0fe', borderRadius: '6px', cursor: isParsingDocx ? 'not-allowed' : 'pointer', fontWeight: 500, fontSize: '13px', opacity: isParsingDocx ? 0.6 : 1 }} title="Upload DOCX to editor" disabled={isParsingDocx}>
          <Upload size={14} className={isParsingDocx ? 'animate-spin' : ''}/> {isParsingDocx ? 'Parsing...' : 'DOCX'}
        </button>
      </div>

      <div className="sidebar-variables">
        {Object.entries(grouped).map(([category, catVariables]) => {
            const isCollapsed = collapsedCategories.has(category);
            return (<div key={category} className="variable-category">
              <button className="category-header" onClick={() => toggleCategory(category)} type="button">
                {isCollapsed ? (<ChevronRight size={16}/>) : (<ChevronDown size={16}/>)}
                <span className="category-name">{category}</span>
                <span className="category-count">{catVariables.length}</span>
              </button>

              {!isCollapsed && (<div className="category-items">
                  {catVariables.map((variable) => (<div
      key={`${category}-${variable.key || variable.label}`}
      className="variable-item"
      draggable
      onDragStart={(e) => handleDragStart(e, variable)}
      onDragEnd={handleDragEnd}
      onClick={() => onInsertVariable?.(variable)}
      title={`${variable.description || variable.label} (Click or drag to insert)`}
      style={{ cursor: 'grab', position: 'relative' }}
    >
      <GripVertical size={14} className="grip-icon"/>
      <span className="variable-type-badge">
        {getVariableIcon(variable)}
      </span>
      <span className="variable-name" style={{ flex: 1 }}>{variable.label}</span>
    </div>))}
                </div>)}
            </div>);
        })}

        {Object.keys(grouped).length === 0 && (<div className="sidebar-empty">
            <p>No variables match your search.</p>
          </div>)}
      </div>
    </aside>);
}
